import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import AddEmployeeModal from "src/components/EmployeeTable/AddEmployeeModal";
import { useApiClient } from "src/hooks/api/useApiClient";
import { useSafeApiCall } from "src/hooks/api/useSafeApiCall";
import { useDealer } from "src/hooks/useDealer";
import type { EditEmployeeFormData } from "src/types/employee";

const API_PREFIX_PATH = "/dealer-portal/v1";

interface AddEmployeeModalContextType {
  openAddEmployeeModal: () => void;
}

interface AddEmployeeModalProviderProps {
  children: ReactNode;
  onEmployeeAdded?: () => void;
}

const AddEmployeeModalContext =
  createContext<AddEmployeeModalContextType | null>(null);

export const AddEmployeeModalProvider = ({
  children,
  onEmployeeAdded,
}: AddEmployeeModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { dealer_id } = useDealer();
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  const openAddEmployeeModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleSave = async (employeeData: EditEmployeeFormData) => {
    const path = `${API_PREFIX_PATH}/${dealer_id}/employees`;
    const body: Record<string, string | undefined> = {
      first_name: employeeData.first_name,
      last_name: employeeData.last_name,
      email: employeeData.email,
      role: employeeData.role,
      phone_number: employeeData.phone_number,
      title: employeeData.title,
    };

    setIsSaving(true);
    try {
      await safeApiCall(() => fetchFromApi(path, { method: "POST", body }), {
        toastErrorMsg: "Failed to create employee",
      });

      setIsOpen(false);
      onEmployeeAdded?.();
    } catch (error) {
      console.error("Failed to create employee", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AddEmployeeModalContext.Provider value={{ openAddEmployeeModal }}>
      {children}
      <AddEmployeeModal
        open={isOpen}
        onOpenChange={setIsOpen}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </AddEmployeeModalContext.Provider>
  );
};

export const useAddEmployeeModal = () => {
  const context = useContext(AddEmployeeModalContext);
  if (!context) {
    throw new Error(
      "useAddEmployeeModal must be used within an AddEmployeeModalProvider",
    );
  }
  return context;
};
